import { PlusCircleOutlined } from "@ant-design/icons";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useAssignBeaconsToOrganization,
  useAssignConstructionSite,
  useBeacons,
  useOrganizationBeacons,
} from "../../api";
import { Beacon } from "../../api/models";
import { PagedApiState } from "../../api/useApiState";
import { DataRefreshContext } from "../../contexts/dataRefreshContext";
import { useSetting } from "../../util";
import { SelectBeaconsModal } from "./SelectBeaconsModal";

interface AssignBeaconsModalConstructionSiteProps {
  constructionSiteId: string;
  siteBeacons: string[];
  organizationId: string;
  title?: string;
  modalButton?: (openModal: () => void) => React.ReactNode;
}

export function AssignBeaconsModalConstructionSite({
  constructionSiteId,
  siteBeacons,
  title,
  organizationId,
  modalButton,
}: AssignBeaconsModalConstructionSiteProps) {
  const { t } = useTranslation();
  const assignBeaconsToConstructionSite = useAssignConstructionSite();
  const dataRefreshContext = useContext(DataRefreshContext);

  const orgBeacons = useOrganizationBeacons(organizationId);

  const filteredOrgBeacons = orgBeacons.value?.items?.filter(
    (beacon) => beacon.isOn && !siteBeacons.includes(beacon.serial)
  );

  const onAssignBeaconsToConstructionProject = async (
    beaconSerials: string[]
  ) => {
    await assignBeaconsToConstructionSite({
      constructionSiteId: constructionSiteId,
      beaconIds: [...beaconSerials, ...siteBeacons],
    });

    dataRefreshContext.refresh();
  };

  return (
    <SelectBeaconsModal
      title={title ?? t("construction-project:assign-beacons")}
      instruction={t("construction-project:assign-beacons-drawing-tooltip")}
      beacons={PagedState(filteredOrgBeacons) as PagedApiState<Beacon>}
      selectButtonText={(beaconSerials) =>
        t("construction-project:assign-beacons", {
          count: beaconSerials.length,
        })
      }
      selectButtonTitle={(beaconSerials) =>
        t("construction-project:assign-beacons-message", {
          count: beaconSerials.length,
        })
      }
      modalButton={modalButton}
      modalButtonIcon={modalButton ? undefined : <PlusCircleOutlined />}
      onBeaconsSelected={onAssignBeaconsToConstructionProject}
      showPagination={false}
    />
  );
}

interface AssignBeaconsModalOrganizationProps {
  organizationId: string;
}

export function AssignBeaconsModalOrganization({
  organizationId,
}: AssignBeaconsModalOrganizationProps) {
  const { t } = useTranslation();
  const assignBeaconsToOrganization = useAssignBeaconsToOrganization();
  const dataRefreshContext = useContext(DataRefreshContext);

  const beacons = useBeacons(false, undefined, undefined, undefined);

  const onAssignBeaconsToOrganization = async (beaconIds: string[]) => {
    await assignBeaconsToOrganization({ beaconIds, organizationId });
    dataRefreshContext.refresh();
  };

  return (
    <SelectBeaconsModal
      title={t("organization:assign-beacons")}
      instruction={t("organization:assign-beacons-drawing-tooltip")}
      beacons={beacons}
      assignedToOrganization={false}
      selectButtonText={(beaconIds) =>
        t("organization:assign-beacons", { count: beaconIds.length })
      }
      selectButtonTitle={(beaconIds) =>
        t("organization:assign-beacons-message", { count: beaconIds.length })
      }
      modalButtonIcon={<PlusCircleOutlined />}
      onBeaconsSelected={onAssignBeaconsToOrganization}
    />
  );
}

const PagedState = (beacons: Beacon[] | undefined) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [currentPageSize, setCurrentPageSize] = useSetting(
    "default-page-size",
    50
  );

  return {
    currentPage,
    currentPageSize: currentPageSize,
    goToPage: (p: any) => setCurrentPage(p),
    setPageSize: (pageSize: any) => setCurrentPageSize(pageSize),
    value: {
      items: beacons as unknown as Beacon[],
      totalCount: beacons?.length || 0,
    },
    loading: false,
    failed: false,
    lastLoadedAt: new Date(),
    refresh: () => {},
  };
};
